export class OutlineLayer {
  constructor(svgGroup, nodeBoundsById) {
    this.group = svgGroup
    this.nodeBoundsById = nodeBoundsById
    this.outlinedNodeIds = new Set()
    this.paths = []
  }

  outline(nodeId) {
    this.outlinedNodeIds.add(nodeId)
    this.render()
  }

  unoutline(nodeId) {
    this.outlinedNodeIds.delete(nodeId)
    this.render()
  }

  outlineMany(nodeIds) {
    for (let i = 0; i < nodeIds.length; i++) {
      this.outlinedNodeIds.add(nodeIds[i])
    }
    this.render()
  }

  unoutlineMany(nodeIds) {
    for (let i = 0; i < nodeIds.length; i++) {
      this.outlinedNodeIds.delete(nodeIds[i])
    }
    this.render()
  }

  render() {
    this.clearPaths()

    const rects = this.outlinedRects()
    if (rects.length === 0) return

    const pathData = unionPathData(rects)
    const svgNS = "http://www.w3.org/2000/svg"
    for (let i = 0; i < pathData.length; i++) {
      const path = document.createElementNS(svgNS, "path")
      path.setAttribute("d", pathData[i])
      path.setAttribute("fill", "none")
      path.setAttribute("stroke", "#18BC4F")
      path.setAttribute("stroke-width", "4")
      path.setAttribute("vector-effect", "non-scaling-stroke")
      path.setAttribute("style", "pointer-events: none")

      this.group.appendChild(path)
      this.paths.push(path)
    }
  }

  clearPaths() {
    for (let i = 0; i < this.paths.length; i++) {
      const path = this.paths[i]
      if (path.parentNode) {
        path.parentNode.removeChild(path)
      }
    }
    this.paths = []
  }

  outlinedRects() {
    const rects = []
    this.outlinedNodeIds.forEach((nodeId) => {
      const bounds = this.nodeBoundsById[nodeId]
      if (bounds && bounds.width > 0 && bounds.height > 0) {
        rects.push({
          x1: normalizeCoordinate(bounds.x),
          y1: normalizeCoordinate(bounds.y),
          x2: normalizeCoordinate(bounds.x + bounds.width),
          y2: normalizeCoordinate(bounds.y + bounds.height),
        })
      }
    })
    return rects
  }
}

// Return a list of SVG path data that can be used to outline the given
// rectangles.  That is, SVG path data for the boundary of the union of the
// given rectangles.
//
// If two rectangles share an edge (or overlap), that is to say they are
// contiguous, their internal edges are not included in the returned paths.
// Multiple path data can be returned if not all of the rectangles are
// contiguous.
//
// The exact algorithm is more general than it needs to be, but is unlikely to
// need to change should additional features be added to the VDC.
//
// It uses coordinate compression: rectangle edges define a compact grid. The
// covered cells identify the union, exposed cell edges define the boundary,
// and those edges are stitched into SVG paths.
//
// For more details on coordinate compression, see:
//
// https://medium.com/algorithms-digest/coordinate-compression-2fff95326fb
// https://stackoverflow.com/questions/29528934/coordinate-compression
function unionPathData(rects) {
  const xs = sortedUnique(rects.flatMap((rect) => [rect.x1, rect.x2]))
  const ys = sortedUnique(rects.flatMap((rect) => [rect.y1, rect.y2]))
  const insideCells = coveredCells(rects, xs, ys)
  const segments = boundarySegments(insideCells, xs, ys)

  return stitchSegments(segments)
}

// Round geometry values so tiny floating point differences do not create false
// gaps.
function normalizeCoordinate(value) {
  return Math.round(value * 1000000) / 1000000
}

function sortedUnique(values) {
  return Array.from(new Set(values)).sort((a, b) => a - b)
}

// Return a set of compressed-grid cells whose midpoint lies inside at least
// one rectangle.
function coveredCells(rects, xs, ys) {
  const cells = new Set()
  for (let xIndex = 0; xIndex < xs.length - 1; xIndex++) {
    for (let yIndex = 0; yIndex < ys.length - 1; yIndex++) {
      const x = (xs[xIndex] + xs[xIndex + 1]) / 2
      const y = (ys[yIndex] + ys[yIndex + 1]) / 2
      if (rects.some((rect) => pointInRect(x, y, rect))) {
        cells.add(cellKey(xIndex, yIndex))
      }
    }
  }
  return cells
}

// Test whether a point lies inside or on the edge of a normalized rectangle.
function pointInRect(x, y, rect) {
  return x >= rect.x1 && x <= rect.x2 && y >= rect.y1 && y <= rect.y2
}

// Emit every covered cell edge that faces an uncovered neighboring cell.
function boundarySegments(insideCells, xs, ys) {
  const segments = []
  insideCells.forEach((key) => {
    const [xIndex, yIndex] = key.split(",").map(Number)
    const x1 = xs[xIndex]
    const x2 = xs[xIndex + 1]
    const y1 = ys[yIndex]
    const y2 = ys[yIndex + 1]

    if (!insideCells.has(cellKey(xIndex, yIndex - 1))) {
      segments.push(segment(x1, y1, x2, y1))
    }
    if (!insideCells.has(cellKey(xIndex + 1, yIndex))) {
      segments.push(segment(x2, y1, x2, y2))
    }
    if (!insideCells.has(cellKey(xIndex, yIndex + 1))) {
      segments.push(segment(x2, y2, x1, y2))
    }
    if (!insideCells.has(cellKey(xIndex - 1, yIndex))) {
      segments.push(segment(x1, y2, x1, y1))
    }
  })
  return segments
}

// Connect boundary segments end-to-start, producing one closed point loop per
// component.
function stitchSegments(segments) {
  const byStart = new Map()
  for (let i = 0; i < segments.length; i++) {
    const key = pointKey(segments[i].start)
    if (!byStart.has(key)) {
      byStart.set(key, [])
    }
    byStart.get(key).push(i)
  }

  const used = new Set()
  const paths = []
  for (let i = 0; i < segments.length; i++) {
    if (used.has(i)) continue

    used.add(i)
    const start = segments[i].start
    let current = segments[i].end
    const points = [start, current]

    while (pointKey(current) !== pointKey(start)) {
      const nextIndex = nextUnusedSegment(byStart, used, current)
      if (nextIndex == null) break

      used.add(nextIndex)
      current = segments[nextIndex].end
      points.push(current)
    }

    paths.push(pathData(points))
  }
  return paths
}

function nextUnusedSegment(byStart, used, point) {
  const candidates = byStart.get(pointKey(point)) || []
  for (let i = 0; i < candidates.length; i++) {
    if (!used.has(candidates[i])) {
      return candidates[i]
    }
  }
  return null
}

// Convert a single closed point loop into SVG path data.
function pathData(points) {
  const commands = [`M ${points[0].x} ${points[0].y}`]
  for (let i = 1; i < points.length; i++) {
    commands.push(`L ${points[i].x} ${points[i].y}`)
  }
  commands.push("Z")
  return commands.join(" ")
}

// Return a directed line segment between two points.
function segment(x1, y1, x2, y2) {
  return {
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
  }
}

// Encode a compressed-grid cell coordinate for Set membership checks.
function cellKey(xIndex, yIndex) {
  return `${xIndex},${yIndex}`
}

// Encode an SVG point coordinate for Map lookups.
function pointKey(point) {
  return `${point.x},${point.y}`
}
